import { api } from "#/lib/api-client";
import type {
  CategoryPaymentSummary,
  MemberCompany,
  MemberContact,
  MemberDocument,
  MemberFeeRecord,
  MemberIctAccess,
  MemberPaymentRecord,
  MemberPaymentRow,
  MemberStatus,
  MembershipApplication,
  MembershipAutomationRule,
  MembershipCategory,
  MembershipFeeType,

  MembershipSource,
  MembershipStats,
  MembershipWorkflowStage,
  PageResponse,
  PaymentStatus,
  ReportMemberStatus,
  TradeFollowUp,
  UploadResult,
  YearPaymentSummary,
  YearlySummaryResponse,
} from "./types";

// ─── Download helpers ─────────────────────────────────────────────────────────
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function triggerDownload(data: unknown, filename: string): void {
  const blob = data instanceof Blob ? data : new Blob([data as BlobPart]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const BASE_URL = "/api/hr/membership";

/**
 * Generic response extractor
 */
const unwrap = <T>(promise: Promise<{ data: T }>): Promise<T> =>
  promise.then((res) => res.data);

/**
 * Normalises a backend response that should be a plain array.
 * Tries multiple envelope shapes so local and production backends
 * can differ without breaking the UI.
 *
 * Priority order:
 *  1. Plain array                          → d
 *  2. Spring Page (flat)                   → d.content
 *  3. Envelope { data: [] }               → d.data
 *  4. Wrapped Spring Page { data.content } → d.data.content
 *  5. Other common keys (items, records…)  → d[key]
 *  6. Last resort: first array value found in the object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrapArray<T>(promise: Promise<{ data: any }>): Promise<T[]> {
  return promise.then((res) => {
    const d = res.data;
    console.debug("[membership:unwrapArray] raw →", JSON.parse(JSON.stringify(d ?? null)));

    // 1. Direct array
    if (Array.isArray(d)) return d as T[];

    // 2. Spring Page flat: { content: [...] }
    if (Array.isArray(d?.content)) return d.content as T[];

    // 3. Envelope: { data: [...] }
    if (Array.isArray(d?.data)) return d.data as T[];

    // 4. Wrapped Spring Page: { data: { content: [...] } }
    if (Array.isArray(d?.data?.content)) return d.data.content as T[];

    // 5. Other common envelope keys
    for (const key of ["items", "records", "list", "results", "payload", "body", "rows", "values"]) {
      if (Array.isArray((d as any)?.[key])) return (d as any)[key] as T[];
    }

    // 6. Last resort: find the first array anywhere in the top-level object
    if (d && typeof d === "object") {
      for (const val of Object.values(d)) {
        if (Array.isArray(val) && (val as any[]).length > 0) return val as T[];
      }
    }

    console.warn("[membership:unwrapArray] could not find array in response — returning []", d);
    return [] as T[];
  });
}

/**
 * Normalises a paginated Spring Boot response into a flat PageResponse<T>.
 * Handles Spring Boot 3.3+ ({ content, page: { totalElements, ... } })
 * and envelope wrappers ({ data: { content, page: { ... } } }).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(promise: Promise<{ data: any }>): Promise<PageResponse<T>> {
  return promise.then((res) => {
    const raw = res.data;
    console.debug("[membership:normalizePage] raw response →", JSON.parse(JSON.stringify(raw ?? null)));
    const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
    const meta = body?.page ?? body;
    const result = {
      content:       body?.content       ?? [],
      totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
      totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
      number:        meta?.number        ?? 0,
      size:          meta?.size          ?? 0,
      first:         meta?.number === 0,
      last:          (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? meta?.total_pages ?? 1),
    };
    console.debug("[membership:normalizePage] parsed →", result);
    return result;
  });
}

/**
 * Standard query params
 */
type PageParams = {
  page?: number;
  size?: number;
  search?: string;
};

/**
 * Strip falsy string values (empty string, null, undefined) from a params
 * object so they are omitted from the query string entirely.
 * This prevents the backend from receiving `search=""` and attempting
 * `lower(bytea)` on PostgreSQL columns that Hibernate maps as bytea.
 */
function cleanParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key in params) {
    const val = params[key];
    if (val !== "" && val !== null && val !== undefined) {
      out[key] = val;
    }
  }
  return out;
}

/**
 * ===============================
 * MEMBERSHIP API
 * ===============================
 */
export const membershipApi = {
  /** DASHBOARD */
  getStats: () =>
    api.get(`${BASE_URL}/dashboard/stats`).then((res) => {
      const d = res.data;
      console.debug("[membership:getStats] raw →", JSON.parse(JSON.stringify(d ?? null)));
      // Handle plain object or envelope { data: {...} }
      const result = (d?.data !== undefined && typeof d.data === "object" ? d.data : d) as MembershipStats;
      console.debug("[membership:getStats] parsed →", result);
      return result;
    }),

  /** ===============================
   * CATEGORIES
   * =============================== */
  listCategories: () =>
    unwrapArray<MembershipCategory>(api.get(`${BASE_URL}/categories`)),

  createCategory: (payload: Partial<MembershipCategory>) =>
    unwrap(api.post<MembershipCategory>(`${BASE_URL}/categories`, payload)),

  updateCategory: (categoryId: number, payload: Partial<MembershipCategory>) =>
    unwrap(
      api.put<MembershipCategory>(
        `${BASE_URL}/categories/${categoryId}`,
        payload
      )
    ),

  /** ===============================
   * FEE TYPES
   * =============================== */
  listFeeTypes: () =>
    unwrapArray<MembershipFeeType>(api.get(`${BASE_URL}/fee-types`)),

  createFeeType: (payload: Partial<MembershipFeeType>) =>
    unwrap(api.post<MembershipFeeType>(`${BASE_URL}/fee-types`, payload)),

  updateFeeType: (feeTypeId: number, payload: Partial<MembershipFeeType>) =>
    unwrap(
      api.put<MembershipFeeType>(
        `${BASE_URL}/fee-types/${feeTypeId}`,
        payload
      )
    ),

  deleteFeeType: (feeTypeId: number) =>
    api.delete(`${BASE_URL}/fee-types/${feeTypeId}`).then(() => undefined),

  /** ===============================
   * APPLICATIONS
   * =============================== */
  listApplications: (
    params?: PageParams & {
      status?: MemberStatus | "";
      source?: MembershipSource | "";
      converted?: boolean;
    }
  ) =>
    normalizePage<MembershipApplication>(
      api.get(
        `${BASE_URL}/applications`,
        { params: params ? cleanParams(params) : undefined }
      )
    ),

  createApplication: (payload: Partial<MembershipApplication>) =>
    unwrap(api.post<MembershipApplication>(`${BASE_URL}/applications`, payload)),

  reviewApplication: (applicationId: number, notes?: string) =>
    unwrap(
      api.post<MembershipApplication>(
        `${BASE_URL}/applications/${applicationId}/review`,
        null,
        { params: { notes } }
      )
    ),

  convertApplicationToMember: (applicationId: number) =>
    unwrap(
      api.post<MemberCompany>(
        `${BASE_URL}/applications/${applicationId}/convert`
      )
    ),

  /** ===============================
   * MEMBERS
   * =============================== */
  listMembers: (
    params?: PageParams & {
      status?: MemberStatus | "";
      categoryId?: number | "";
      workflowStage?: MembershipWorkflowStage | "";
      /** Filter by the calendar year the member was activated (backend to support) */
      membershipYear?: number | "";
    }
  ) =>
    normalizePage<MemberCompany>(
      api.get(
        `${BASE_URL}/members`,
        { params: params ? cleanParams(params) : undefined }
      )
    ),

  getMember: (memberId: number) =>
    unwrap(api.get<MemberCompany>(`${BASE_URL}/members/${memberId}`)),

  createMember: (payload: Partial<MemberCompany>) =>
    unwrap(api.post<MemberCompany>(`${BASE_URL}/members`, payload)),

  updateMember: (memberId: number, payload: Partial<MemberCompany>) =>
    unwrap(
      api.put<MemberCompany>(
        `${BASE_URL}/members/${memberId}`,
        payload
      )
    ),

  submitToBoard: (memberId: number, notes?: string) =>
    unwrap(
      api.post<MemberCompany>(
        `${BASE_URL}/members/${memberId}/submit-to-board`,
        null,
        { params: { notes } }
      )
    ),

  updateBoardDecision: (
    memberId: number,
    payload: { approved: boolean; notes?: string }
  ) =>
    unwrap(
      api.post<MemberCompany>(
        `${BASE_URL}/members/${memberId}/board-decision`,
        payload
      )
    ),

  updateTraining: (
    memberId: number,
    payload: { completed: boolean; notes?: string }
  ) =>
    unwrap(
      api.post<MemberCompany>(
        `${BASE_URL}/members/${memberId}/training`,
        payload
      )
    ),

  /** ===============================
   * CONTACTS
   * =============================== */
  listContacts: (memberId: number) =>
    unwrap(
      api.get<MemberContact[]>(
        `${BASE_URL}/members/${memberId}/contacts`
      )
    ),

  addContact: (memberId: number, payload: Partial<MemberContact>) =>
    unwrap(
      api.post<MemberContact>(
        `${BASE_URL}/members/${memberId}/contacts`,
        payload
      )
    ),

  /** ===============================
   * FEES
   * =============================== */
  listFees: (memberId: number) =>
    unwrap(
      api.get<MemberFeeRecord[]>(
        `${BASE_URL}/members/${memberId}/fees`
      )
    ),

  createFeeRecord: (memberId: number, payload: Partial<MemberFeeRecord>) =>
    unwrap(
      api.post<MemberFeeRecord>(
        `${BASE_URL}/members/${memberId}/fees`,
        payload
      )
    ),

  updateFeeRecord: (feeId: number, payload: Partial<MemberFeeRecord>) =>
    unwrap(
      api.put<MemberFeeRecord>(
        `${BASE_URL}/fees/${feeId}`,
        payload
      )
    ),

  /** ===============================
   * DOCUMENTS
   * =============================== */
  listDocuments: (memberId: number) =>
    unwrap(
      api.get<MemberDocument[]>(
        `${BASE_URL}/members/${memberId}/documents`
      )
    ),

  createDocument: (memberId: number, payload: Partial<MemberDocument>) =>
    unwrap(
      api.post<MemberDocument>(
        `${BASE_URL}/members/${memberId}/documents`,
        payload
      )
    ),

  updateDocument: (documentId: number, payload: Partial<MemberDocument>) =>
    unwrap(
      api.put<MemberDocument>(
        `${BASE_URL}/documents/${documentId}`,
        payload
      )
    ),

  /** ===============================
   * ICT ACCESS
   * =============================== */
  getIctAccess: (memberId: number) =>
    unwrap(
      api.get<MemberIctAccess>(
        `${BASE_URL}/members/${memberId}/ict-access`
      )
    ),

  updateIctAccess: (memberId: number, payload: Partial<MemberIctAccess>) =>
    unwrap(
      api.put<MemberIctAccess>(
        `${BASE_URL}/members/${memberId}/ict-access`,
        payload
      )
    ),

  /** ===============================
   * TRADE FOLLOW UPS
   * =============================== */
  listTradeFollowUps: (memberId: number) =>
    unwrap(
      api.get<TradeFollowUp[]>(
        `${BASE_URL}/members/${memberId}/trade-follow-ups`
      )
    ),

  addTradeFollowUp: (memberId: number, payload: Partial<TradeFollowUp>) =>
    unwrap(
      api.post<TradeFollowUp>(
        `${BASE_URL}/members/${memberId}/trade-follow-ups`,
        payload
      )
    ),

  /** ===============================
   * BULK UPLOAD
   * =============================== */
  bulkUploadMembers: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    return unwrap(
      api.post<UploadResult>(
        `${BASE_URL}/members/bulk-upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      )
    );
  },

  /**
   * Was window.open() on a raw API URL — bypassed the axios instance entirely, so the Bearer
   * auth header never made it onto the request. Fixed 2026-07-13 to use the same authenticated
   * blob-fetch pattern as every other download in this file.
   */
  downloadTemplate: async (): Promise<void> => {
    const res = await api.get(`${BASE_URL}/members/template`, { responseType: "blob" });
    triggerDownload(res.data, "membership-import-template.xlsx");
  },

  /** ===============================
   * AUTOMATION RULES
   * =============================== */
  listAutomationRules: () =>
    unwrap(
      api.get<MembershipAutomationRule[]>(
        `${BASE_URL}/automation-rules`
      )
    ),

  createAutomationRule: (payload: Partial<MembershipAutomationRule>) =>
    unwrap(
      api.post<MembershipAutomationRule>(
        `${BASE_URL}/automation-rules`,
        payload
      )
    ),

  /** ===============================
   * PAYMENT ANALYTICS
   * =============================== */

  /**
   * GET /api/hr/membership/reports/category-payments
   * Fee collection totals grouped by membership category.
   * @param financialYear  e.g. "2025/2026" — omit for all years combined
   */
  getCategoryPayments: (params: { financialYear?: string }) =>
    unwrapArray<CategoryPaymentSummary>(
      api.get(
        `${BASE_URL}/reports/category-payments`,
        { params: cleanParams(params) }
      )
    ),

  /**
   * GET /api/hr/membership/reports/member-payments
   * Per-member payment status rows (flat, not paginated).
   * Rows where paymentStatus is null have no fee record for the year → "Not Invoiced".
   */
  getReportMemberPayments: (params: {
    financialYear?: string;
    status?: ReportMemberStatus | "";
  }) =>
    unwrapArray<MemberPaymentRow>(
      api.get(
        `${BASE_URL}/reports/member-payments`,
        { params: cleanParams(params) }
      )
    ),

  /**
   * GET /api/hr/membership/reports/member-payments (legacy paginated version)
   * @deprecated use getReportMemberPayments
   */
  listMemberPayments: (params: {
    financialYear?: string;
    categoryId?: number | "";
    paymentStatus?: PaymentStatus | "";
    fromDate?: string;
    toDate?: string;
    search?: string;
    page?: number;
    size?: number;
  }) =>
    normalizePage<MemberPaymentRecord>(
      api.get(
        `${BASE_URL}/reports/member-payments`,
        { params: cleanParams(params) }
      )
    ),

  /**
   * GET /api/hr/membership/reports/yearly-summary
   * Board-approval cohort sizes and fee totals per membership year, descending.
   */
  getYearlySummary: () =>
    unwrapArray<YearlySummaryResponse>(
      api.get(`${BASE_URL}/reports/yearly-summary`)
    ),

  // ─── Server-side Excel / PDF exports ─────────────────────────────────────────

  /**
   * GET /api/hr/membership/reports/members/excel
   * Downloads membership-members-YYYY-MM-DD.xlsx
   */
  downloadMembersExcel: async (
    membershipYear?: number,
    status?: ReportMemberStatus,
  ): Promise<void> => {
    const res = await api.get(`${BASE_URL}/reports/members/excel`, {
      params: cleanParams({ membershipYear, status }),
      responseType: "blob",
    });
    triggerDownload(res.data, `membership-members-${todayStr()}.xlsx`);
  },

  /**
   * GET /api/hr/membership/reports/members/pdf
   * Downloads membership-members-YYYY-MM-DD.pdf (landscape A4)
   */
  downloadMembersPdf: async (
    membershipYear?: number,
    status?: ReportMemberStatus,
  ): Promise<void> => {
    const res = await api.get(`${BASE_URL}/reports/members/pdf`, {
      params: cleanParams({ membershipYear, status }),
      responseType: "blob",
    });
    triggerDownload(res.data, `membership-members-${todayStr()}.pdf`);
  },

  /**
   * GET /api/hr/membership/reports/payments/excel
   * Downloads membership-payments-YYYY-MM-DD.xlsx
   */
  downloadPaymentsExcel: async (financialYear?: string): Promise<void> => {
    const res = await api.get(`${BASE_URL}/reports/payments/excel`, {
      params: cleanParams({ financialYear }),
      responseType: "blob",
    });
    triggerDownload(res.data, `membership-payments-${todayStr()}.xlsx`);
  },

  /**
   * GET /api/hr/membership/reports/payments/pdf
   * Downloads membership-payments-YYYY-MM-DD.pdf (landscape A4)
   */
  downloadPaymentsPdf: async (financialYear?: string): Promise<void> => {
    const res = await api.get(`${BASE_URL}/reports/payments/pdf`, {
      params: cleanParams({ financialYear }),
      responseType: "blob",
    });
    triggerDownload(res.data, `membership-payments-${todayStr()}.pdf`);
  },
};